import React, { useState, useEffect, useMemo } from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';
import CloseIcon from './icons/CloseIcon';

const createStyles = (theme: Theme) => ({
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    overflowY: 'auto' as const,
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
    zIndex: 50,
    transition: `opacity ${theme.motion.duration.normal}ms ${theme.motion.easing.standard}`,
    backgroundColor: theme.colors.modalOverlay,
    opacity: 1,
    pointerEvents: 'auto' as const,
  },
  overlayHidden: {
    backgroundColor: 'transparent',
    opacity: 0,
    pointerEvents: 'none' as const,
  },
  modalContent: {
    backgroundColor: theme.colors.modalBackground,
    padding: theme.spacing.xl,
    borderRadius: theme.radii.lg,
    boxShadow: theme.shadows.modal,
    width: '100%',
    maxWidth: '28rem', // 448px equivalent to max-w-md
    transition: `all ${theme.motion.duration.normal}ms ${theme.motion.easing.standard} transform`,
    opacity: 1,
    transform: 'scale(1)',
  },
  modalContentHidden: {
    opacity: 0,
    transform: 'scale(0.95)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    margin: 0,
  },
  closeButton: {
    color: theme.colors.textSecondary,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing.xs,
    transition: `color ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonHover: {
    color: theme.colors.text,
  },
  message: {
    color: theme.colors.text,
    marginBottom: theme.spacing.xl,
    margin: 0,
    fontSize: theme.typography.sizes.md,
    lineHeight: theme.typography.lineHeights.normal,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing.sm,
  },
  button: {
    paddingLeft: theme.spacing.lg,
    paddingRight: theme.spacing.lg,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    fontSize: theme.typography.sizes.sm,
    fontWeight: theme.typography.weights.medium,
    borderRadius: theme.radii.md,
    border: 'none',
    cursor: 'pointer',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
    outline: 'none',
  },
  buttonCancel: {
    color: theme.colors.text,
    backgroundColor: theme.colors.surfaceSecondary,
  },
  buttonCancelHover: {
    backgroundColor: theme.colors.surfaceTertiary,
  },
  buttonConfirm: {
    color: theme.colors.textInverse,
    backgroundColor: theme.colors.buttonPrimary,
  },
  buttonConfirmHover: {
    backgroundColor: theme.colors.buttonPrimaryHover,
  },
  buttonAlt: {
    color: theme.colors.textInverse,
    backgroundColor: theme.colors.buttonError,
  },
  buttonAltHover: {
    backgroundColor: theme.colors.buttonErrorHover,
  },
});

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  onAltConfirm?: () => void; // Optional: For a third action button
  altConfirmText?: string; // Optional: Text for the third action button
  showCancelButton?: boolean; // Optional: false for a notice with a single acknowledge button
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onAltConfirm,
  altConfirmText = 'Alternative Action',
  showCancelButton = true,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Delay showing content slightly to allow overlay to fade in first if desired,
      // or set immediately if overlay and content animate together.
      // For simplicity, setting immediately and relying on CSS for simultaneous animation.
      setShowContent(true);
    } else {
      setShowContent(false); // Start hide animation for content if isOpen becomes false
    }
  }, [isOpen]);

  const handleConfirm = () => {
    setShowContent(false); // Start exit animation for content
    setTimeout(onConfirm, 250); // Call onConfirm from App.tsx after animation (matches motion.duration.normal)
  };

  const handleCancel = () => {
    setShowContent(false); // Start exit animation for content
    setTimeout(onCancel, 250); // Call onCancel from App.tsx after animation (matches motion.duration.normal)
  };

  const handleAltConfirm = () => {
    if (onAltConfirm) {
      setShowContent(false); // Start exit animation for content
      setTimeout(onAltConfirm, 250); // Call onAltConfirm from App.tsx after animation (matches motion.duration.normal)
    }
  };

  if (!isOpen && !showContent) { // Only render nothing if fully closed and content animation would be done
      // This condition might need tweaking based on how App.tsx handles isOpen for ConfirmationModal
      // If App.tsx unmounts ConfirmationModal when isOpen is false, then internal showContent for exit anim might be cut short.
      // The current App.tsx setup for ConfirmationModal keeps it mounted but passes isOpen.
      // So, the modal's own opacity transition (tied to isOpen via showContent logic) should work.
    return null;
  }

  return (
    <div
      style={{
        ...styles.overlay,
        ...(isOpen ? {} : styles.overlayHidden),
      }}
    >
      <div
        style={{
          ...styles.modalContent,
          ...(showContent && isOpen ? {} : styles.modalContentHidden),
        }}
      >
        <div style={styles.header}>
          <h2 style={styles.title}>{title}</h2>
          <button
            onClick={handleCancel}
            style={styles.closeButton}
            aria-label="Close modal"
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, {
                ...styles.closeButton,
                ...styles.closeButtonHover,
              });
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, styles.closeButton);
            }}
          >
            <CloseIcon size={24} color="currentColor" />
          </button>
        </div>
        <p style={styles.message}>{message}</p>
        <div style={styles.buttonContainer}>
          {showCancelButton && <button
            onClick={handleCancel}
            style={{
              ...styles.button,
              ...styles.buttonCancel,
            }}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, {
                ...styles.button,
                ...styles.buttonCancel,
                ...styles.buttonCancelHover,
              });
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, {
                ...styles.button,
                ...styles.buttonCancel,
              });
            }}
          >
            {cancelText}
          </button>}
          <button
            onClick={handleConfirm}
            style={{
              ...styles.button,
              ...styles.buttonConfirm,
            }}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, {
                ...styles.button,
                ...styles.buttonConfirm,
                ...styles.buttonConfirmHover,
              });
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, {
                ...styles.button,
                ...styles.buttonConfirm,
              });
            }}
          >
            {confirmText}
          </button>
          {onAltConfirm && (
            <button
              onClick={handleAltConfirm}
              style={{
                ...styles.button,
                ...styles.buttonAlt,
              }}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, {
                  ...styles.button,
                  ...styles.buttonAlt,
                  ...styles.buttonAltHover,
                });
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, {
                  ...styles.button,
                  ...styles.buttonAlt,
                });
              }}
            >
              {altConfirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
